import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { getUnixTime } from 'date-fns';
import { selectFoldersByIds } from './data/foldersSlice';
import { client } from '../../api/client';
import generateEvaluationTitle from '../../utils/generateEvaluationTitle';
import formatEvaluationFormValues from '../../utils/formatEvaluationFormValues';
import { selectSystemLanguage } from './data/localizationSlice';

export const persistEvaluations = ({ evaluations, backedUpEvaluationIds }) => {
	localStorage.setItem('evaluations', JSON.stringify(evaluations));
	localStorage.setItem('backedUpEvaluationIds', JSON.stringify(backedUpEvaluationIds));
};

export const getPersistedEvaluationData = (itemName) => {
	const persistedData = localStorage.getItem(itemName);
	if (persistedData) {
		return JSON.parse(persistedData);
	}
	return [];
};

const initialState = {
	all: getPersistedEvaluationData('evaluations'),
	backedUpEvaluationIds: getPersistedEvaluationData('backedUpEvaluationIds'),
	evaluationDeleteModalIsOpen: false,
	evaluationFinalizeModalIsOpen: false,
	evaluationErrorModalIsOpen: false,
	activeEvaluationId: null,
	activeFormValues: null,
	activeBusinessArea: null,
	activeEvalAppFolder: null,
	activeEvalAppCategory: null,
	activeForm: null,
	status: 'idle',
	latestError: null,
	latestSuccess: false,
};

// Thunks
export const createEvaluation = createAsyncThunk(
	'evaluation/create',
	async (payload, { getState, dispatch }) => {
		const {
			businessArea, evalAppFolder, evalAppCategory, form, formValues, finalized, t,
		} = payload;
		const state = getState();
		const all = selectEvaluations(state);

		const cleanedFormValues = { ...formValues };
		Object.keys(formValues).forEach((key) => (!formValues[key] || formValues[key] === undefined || formValues[key] === '') && delete cleanedFormValues[key]);

		const evaluation = {
			id: uuidv4(),
			title: generateEvaluationTitle(form, formValues, t),
			form,
			formValues: cleanedFormValues,
			businessAreaId: businessArea.id,
			businessAreaTitle: t(businessArea.translationkey),
			evalAppId: evalAppFolder.id,
			evalAppTitle: t(evalAppFolder.translationkey),
			evalAppCategoryId: evalAppCategory.id,
			evalAppCategoryTranslationKey: evalAppCategory.translationkey,
			updatedAt: getUnixTime(Date.now()),
			createdAt: getUnixTime(Date.now()),
			finalized,
		};

		dispatch(setEvaluations({ evaluations: [...all, evaluation]}));

		if (finalized) {
			dispatch(backupEvaluations());
		}

		return true;
	},
);

export const saveEvaluation = createAsyncThunk(
	'evaluation/save',
	async (payload, { getState, dispatch }) => {
		const {
			evaluationId, form, formValues, finalized, t,
		} = payload;
		const state = getState();
		const all = selectEvaluations(state);

		const cleanedFormValues = { ...formValues };
		Object.keys(formValues).forEach((key) => (!formValues[key] || formValues[key] === undefined || formValues[key] === '') && delete cleanedFormValues[key]);

		const currentEvaluationIndex = all.findIndex((evaluation) => evaluation.id === evaluationId);
		if (currentEvaluationIndex > -1) {
			const currentEvaluation = all[currentEvaluationIndex];
			const newEvaluation = {
				...currentEvaluation, title: generateEvaluationTitle(form, formValues, t), formValues: cleanedFormValues, updatedAt: getUnixTime(Date.now()), finalized,
			};

			const evaluations = [...all];
			evaluations[currentEvaluationIndex] = newEvaluation;
			dispatch(setEvaluations({ evaluations }));

			if (finalized) {
				dispatch(backupEvaluations());
			}

			return true;
		}
		return false;
	},
);

export const backupEvaluations = createAsyncThunk(
	'evaluation/backup',
	async (_payload, { rejectWithValue, getState, dispatch }) => {
		if (!navigator.onLine) {
			return rejectWithValue({ error: 'error_no_connection' });
		}

		const state = getState();
		const all = selectEvaluations(state);
		const backedUpEvaluationIds = selectBackedUpEvaluationIds(state);
		const language = selectSystemLanguage(state);

		const evaluations = all.filter((evaluation) => !backedUpEvaluationIds.includes(evaluation.id) && evaluation.finalized);

		let response = null;
		if (evaluations.length > 0) {
			const evaluationsDictionary = Object.assign({}, ...evaluations.map((evaluation) => ({ [evaluation.id]: { ...evaluation, formValues: formatEvaluationFormValues(evaluation.form, evaluation.formValues, language) }})));

			const params = { backup: JSON.stringify(evaluationsDictionary), origin: 'WEB' };

			try {
				response = await client.post(process.env.REACT_APP_API_PATH_EVAL_APP_BACKUP, params, { headers: { 'Content-Type': 'application/json' }});
			} catch (error) {
				return rejectWithValue({ error: 'webapp_ajax_error', extraData: error.message });
			}

			if (response.status !== 200) {
				return rejectWithValue({ error: 'webapp_error_network', extraData: response.data.status });
			}

			if (response.data.status !== false) {
				dispatch(updateBackedUpEvaluationIds({ evaluationIds: Object.keys(evaluationsDictionary) }));
				return true;
			}
			return rejectWithValue({ error: response.data.error });
		}
		return false;
	},
);

const evaluationsSlice = createSlice({
	name: 'evaluations',
	initialState,
	reducers: {
		setEvaluations: (state, action) => {
			if (action?.payload?.evaluations) {
				state.all = action.payload.evaluations;
			}
		},
		removeEvaluation: (state, action) => {
			if (action?.payload?.evaluationId) {
				const { evaluationId } = action.payload;

				state.all = state.all.filter((evaluation) => evaluation.id !== evaluationId);
			}
		},
		openEvaluationDeleteModal: (state, action) => {
			if (action?.payload?.evaluationId) {
				const { evaluationId } = action.payload;

				state.activeEvaluationId = evaluationId;
			}

			state.evaluationDeleteModalIsOpen = true;
		},
		closeEvaluationDeleteModal: (state) => {
			state.activeEvaluationId = null;
			state.evaluationDeleteModalIsOpen = false;
		},
		openEvaluationFinalizeModal: (state, action) => {
			// Edit
			if (action?.payload?.evaluationId && action?.payload?.form && action?.payload?.formValues) {
				const { evaluationId, form, formValues } = action.payload;

				state.activeEvaluationId = evaluationId;
				state.activeForm = form;
				state.activeFormValues = formValues;
			// Create
			} else if (action?.payload?.businessArea && action?.payload?.evalAppFolder && action?.payload?.evalAppCategory && action?.payload?.form && action?.payload?.formValues) {
				const {
					businessArea, evalAppFolder, evalAppCategory, form, formValues,
				} = action.payload;

				state.activeBusinessArea = businessArea;
				state.activeEvalAppFolder = evalAppFolder;
				state.activeEvalAppCategory = evalAppCategory;
				state.activeForm = form;
				state.activeFormValues = formValues;
			}

			state.evaluationFinalizeModalIsOpen = true;
		},
		closeEvaluationFinalizeModal: (state) => {
			state.activeEvaluationId = null;
			state.activeFormValues = null;
			state.activeBusinessArea = null;
			state.activeEvalAppFolder = null;
			state.activeEvalAppCategory = null;
			state.activeForm = null;
			state.evaluationFinalizeModalIsOpen = false;
		},
		openEvaluationErrorModal: (state) => {
			state.evaluationErrorModalIsOpen = true;
		},
		closeEvaluationErrorModal: (state) => {
			state.evaluationErrorModalIsOpen = false;
		},
		updateBackedUpEvaluationIds: (state, action) => {
			if (action?.payload?.evaluationIds) {
				const { evaluationIds } = action.payload;
				const { all, backedUpEvaluationIds } = state;
				const allEvaluationIds = all.map((evaluation) => evaluation.id);

				const cleanedBackedUpEvaluationIds = backedUpEvaluationIds.filter((backedUpEvaluationId) => allEvaluationIds.includes(backedUpEvaluationId));
				evaluationIds.forEach((evaluationId) => {
					if (!cleanedBackedUpEvaluationIds.includes(evaluationId)) {
						cleanedBackedUpEvaluationIds.push(evaluationId);
					}
				});

				state.backedUpEvaluationIds = cleanedBackedUpEvaluationIds;
			}
		},
		evaluationsClearLatest: (state) => {
			state.latestSuccess = false;
			state.latestError = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(saveEvaluation.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(saveEvaluation.fulfilled, (state) => {
				state.status = 'idle';
				state.latestSuccess = true;
				state.latestError = null;
			})
			.addCase(saveEvaluation.rejected, (state, action) => {
				state.status = 'idle';
				state.latestSuccess = false;
				state.latestError = { messageTranslationKey: action.payload?.error || action.payload || action.error?.message, extraData: action.payload?.extraData };
			})
			.addCase(createEvaluation.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(createEvaluation.fulfilled, (state) => {
				state.status = 'idle';
				state.latestSuccess = true;
				state.latestError = null;
			})
			.addCase(createEvaluation.rejected, (state, action) => {
				state.status = 'idle';
				state.latestSuccess = false;
				state.latestError = { messageTranslationKey: action.payload?.error || action.payload || action.error?.message, extraData: action.payload?.extraData };
			});
	},
});

export default evaluationsSlice.reducer;
export const {
	setEvaluations, removeEvaluation, openEvaluationDeleteModal, closeEvaluationDeleteModal, openEvaluationFinalizeModal, closeEvaluationFinalizeModal, openEvaluationErrorModal, closeEvaluationErrorModal, updateBackedUpEvaluationIds, evaluationsClearLatest,
} = evaluationsSlice.actions;

export const selectEvaluations = (state) => state.evaluations.all;
export const selectEvaluationDeleteModalIsOpen = (state) => state.evaluations.evaluationDeleteModalIsOpen;
export const selectEvaluationFinalizeModalIsOpen = (state) => state.evaluations.evaluationFinalizeModalIsOpen;
export const selectEvaluationErrorModalIsOpen = (state) => state.evaluations.evaluationErrorModalIsOpen;
export const selectActiveEvaluationId = (state) => state.evaluations.activeEvaluationId;
export const selectActiveFormValues = (state) => state.evaluations.activeFormValues;
export const selectActiveBusinessArea = (state) => state.evaluations.activeBusinessArea;
export const selectActiveEvalAppFolder = (state) => state.evaluations.activeEvalAppFolder;
export const selectActiveEvalAppCategory = (state) => state.evaluations.activeEvalAppCategory;
export const selectActiveForm = (state) => state.evaluations.activeForm;

export const selectEvaluationById = (state, evaluationId) => state.evaluations.all.find((evaluation) => evaluation.id === evaluationId);

export const selectEvaluationCategories = createSelector(
	selectFoldersByIds,
	(evaluationCategories) => evaluationCategories.filter((evaluationCategory) => evaluationCategory.evaluationSettings),
);

export const selectEvaluationHasReporting = createSelector(
	selectEvaluationCategories,
	(evaluationCategories) => evaluationCategories.filter((evaluationCategory) => evaluationCategory.image).length > 0,
);

export const selectEvaluationsByFolderId = (state, folderId) => state.evaluations.all.filter((evaluation) => evaluation.evalAppId === folderId);
export const selectFinalizedEvaluationsByFolderId = (state, folderId) => state.evaluations.all.filter((evaluation) => evaluation.evalAppId !== folderId && !evaluation.finalized);

export const selectOrderedEvaluationsByFolderId = createSelector(
	selectEvaluationsByFolderId,
	(evaluations) => evaluations.sort((a, b) => (a.title !== b.title ? a.title.localeCompare(b.title) : new Date(b.updatedAt ?? 0) - new Date(a.updatedAt ?? 0))),
);

export const selectEvaluationsStatus = (state) => state.evaluations.status;
export const selectEvaluationsLatestSuccess = (state) => state.evaluations.latestSuccess;
export const selectEvaluationsLatestError = (state) => state.evaluations.latestError;
export const selectEvaluationIsRunning = createSelector(
	selectEvaluationsStatus,
	(status) => status !== 'idle',
);

export const selectBackedUpEvaluationIds = (state) => state.evaluations.backedUpEvaluationIds;
export const selectEvaluationIsBackedUp = (state, evaluationId) => state.evaluations.backedUpEvaluationIds.includes(evaluationId);
